import { wait } from '@change-corgi/core/async';
import type { UtilityContext } from '@change-corgi/core/react/utilityContext';

import type { PetitionInput } from 'src/app/pages/startAPetition/pageContext';

import { getAsyncPetitionData } from './api/getAsyncPetitionData';
import type { StartAPetitionAsyncPetitionDataQuery } from './api/getAsyncPetitionData/api.graphql';
import type {
	SapCreatePublishedPetitionMutation,
	SapCreatePublishedPetitionMutationVariables,
} from './createPublishedPetition.graphql';
import { SAPCreatePublishedPetition } from './createPublishedPetition.graphql';
import { SapPetitionSaveError } from './errors';
import { schema } from './schema';
import { pollUntil } from './utils';

type QualityScore = Omit<
	Extract<
		NonNullable<StartAPetitionAsyncPetitionDataQuery['petition']>['qualityScore'],
		{ __typename: 'QualityScore' }
	>,
	'__typename'
>;
type CreatePublishedPetitionParams = {
	petition: PetitionInput;
	petitionQualityScoreToggle: boolean;
};
type CreatePublishedPetitionReturn = Extract<
	SapCreatePublishedPetitionMutation['createPublishedPetition'],
	{ __typename: 'CreatePublishedPetitionSuccess' }
>['petition'] & {
	qualityScore: QualityScore | null | undefined;
};

export async function createPublishedPetition(
	{ petition, petitionQualityScoreToggle }: CreatePublishedPetitionParams,
	utilityContext: UtilityContext,
): Promise<CreatePublishedPetitionReturn> {
	try {
		const { data: petitionInput, error: validationError } = schema.safeParse(petition);

		// creating a new error with a custom message instead of using ZodError so we can easily search in logs
		if (validationError) throw new SapPetitionSaveError('InvalidInput', { cause: validationError });

		const [{ data }] = await Promise.all([
			utilityContext.gql.fetch<SapCreatePublishedPetitionMutation, SapCreatePublishedPetitionMutationVariables>({
				query: SAPCreatePublishedPetition,
				variables: {
					input: petitionInput,
				},
				rejectOnError: true,
			}),
			// minimum wait time accounts for displaying the loading screen
			wait(5000),
		]);

		if (data.createPublishedPetition.__typename !== 'CreatePublishedPetitionSuccess') {
			const typename = data.createPublishedPetition.__typename;
			throw new SapPetitionSaveError(typename);
		}

		const petitionId = data.createPublishedPetition.petition.id;
		const shouldPollForAsyncData = petitionQualityScoreToggle;
		const polledAsyncData = await pollUntil(
			async () =>
				getAsyncPetitionData(
					{
						petitionId,
						fetchQualityScore: petitionQualityScoreToggle,
					},
					utilityContext,
				),
			{
				enabled: shouldPollForAsyncData,
				interval: 1000,
				// petition quality score takes considerably longer to be available
				// so it's used as the upper limit for the polling timeout
				timeout: petitionQualityScoreToggle ? 40000 : 5000,
			},
		);
		const qualityScore = polledAsyncData && 'qualityScore' in polledAsyncData ? polledAsyncData.qualityScore : null;

		return { ...data.createPublishedPetition.petition, qualityScore };
	} catch (err) {
		const error = err instanceof SapPetitionSaveError ? err : new SapPetitionSaveError(undefined, { cause: err });
		void utilityContext.errorReporter.report({ error });
		throw error;
	}
}
